import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Camera, Heart, ShieldCheck, Star } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ReviewForm } from "@/components/site/ReviewForm";
import { Lightbox, PhotoWall, RatingSummary, ReviewCard } from "@/components/site/ReviewBits";
import { GoogleButtons, GoogleReviews } from "@/components/site/GoogleReviews";
import { PageHero, SectionHead } from "@/components/site/PageHero";
import { api } from "@/lib/api";
import { IMG } from "@/lib/constants";
import { Seo, breadcrumb } from "@/components/site/Seo";

const POINTS = [
  { icon: Camera, t: "Add your travel photos", d: "Your favourite beach, scuba or sunset moments (optional)." },
  { icon: ShieldCheck, t: "Approved before publishing", d: "Our team checks every review before it goes live." },
  { icon: Heart, t: "Help other travellers", d: "Your experience helps families and couples plan better." },
];

const Distribution = ({ reviews }) => (
  <div className="space-y-1.5 w-full max-w-xs" data-testid="reviews-distribution">
    {[5, 4, 3, 2, 1].map((s) => {
      const n = reviews.filter((r) => r.rating === s).length;
      return (
        <div key={s} className="flex items-center gap-2 text-xs text-slate-600">
          <span className="w-6 font-mono">{s}<Star className="inline h-3 w-3 -mt-0.5 fill-amber-400 text-amber-400" /></span>
          <span className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><span className="block h-full bg-amber-400 rounded-full" style={{ width: `${(n / reviews.length) * 100}%` }} /></span>
          <span className="w-6 text-right font-mono">{n}</span>
        </div>
      );
    })}
  </div>
);

const SiteReviews = ({ data, onOpen }) => (
  <section className="container-x pt-20" data-testid="site-reviews-section">
    <SectionHead eyebrow="Website guest reviews" title="Stories shared on our website" subtitle="Reviews and photos submitted by our guests on this website, approved by our team." />
    <div className="mt-8 rounded-2xl border border-slate-200 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-testid="reviews-summary">
      <div>
        <p className="font-mono text-5xl font-semibold text-navy" data-testid="reviews-average">{data.average.toFixed(1)}</p>
        <div className="mt-2"><RatingSummary data={data} testId="reviews-summary-stars" /></div>
      </div>
      <Distribution reviews={data.reviews} />
    </div>
    <div className="mt-8"><PhotoWall reviews={data.reviews} onOpen={onOpen} max={9} /></div>
    <div className="mt-8 columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="reviews-grid">
      {data.reviews.map((r) => <div key={r.id} className="mb-5 break-inside-avoid"><ReviewCard r={r} onPhoto={onOpen} clamp={false} /></div>)}
    </div>
  </section>
);

const WriteSection = () => (
  <section id="write" className="container-x pt-20 scroll-mt-24 grid gap-8 lg:grid-cols-12" data-testid="reviews-write-section">
    <div className="lg:col-span-8 rounded-3xl bg-white border border-slate-200 shadow-xl p-6 sm:p-10">
      <p className="eyebrow">Share your trip photos</p>
      <h2 className="mt-2 font-display text-3xl sm:text-4xl text-navy font-semibold">Post your story on our website</h2>
      <p className="mt-2 mb-8 text-sm text-slate-600">Want your photos on our website too? Send them here. To review us on Google, use the "Write a Review" button above.</p>
      <ReviewForm />
    </div>
    <aside className="lg:col-span-4 space-y-6 lg:pt-24">
      {POINTS.map(({ icon: Icon, t, d }) => (
        <div key={t} className="flex gap-4">
          <span className="h-11 w-11 shrink-0 rounded-xl bg-sky-50 text-ocean grid place-items-center"><Icon className="h-5 w-5" /></span>
          <div><p className="font-semibold text-navy">{t}</p><p className="text-sm text-slate-600">{d}</p></div>
        </div>
      ))}
    </aside>
  </section>
);

export default function GuestReviews() {
  const { hash } = useLocation();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/reviews").then((r) => setData(r.data)).catch(() => setData({ reviews: [], count: 0, average: 0 })); }, []);
  useEffect(() => {
    if (hash === "#write" && data) {
      setTimeout(() => { document.getElementById("write")?.scrollIntoView({ behavior: "smooth" }); }, 100);
    }
  }, [hash, data]);

  return (
    <div data-testid="guest-reviews-page">
      <Seo title="Ocean Wings Travel Reviews | Andaman Trip Guest Reviews"
        description="Read genuine Google and website reviews from travellers who explored Port Blair, Havelock and Neil Island with Ocean Wings Travel, and share your own Andaman trip story."
        path="/reviews" jsonLd={[breadcrumb([["Home", "/"], ["Reviews", "/reviews"]])]} />
      <PageHero img={IMG.lagoon} alt="Turquoise Andaman lagoon" eyebrow="Guest reviews" title="What our guests say" testId="reviews-hero"
        subtitle="Genuine reviews from travellers who explored the Andaman Islands with Ocean Wings Travel.">
        <GoogleButtons light prefix="reviews-hero-google" />
      </PageHero>
      <section className="container-x pt-14" data-testid="google-reviews-section"><GoogleReviews /></section>
      {!data ? <div className="container-x pt-14"><Skeleton className="h-48 rounded-2xl" /></div>
        : data.reviews.length > 0 && <SiteReviews data={data} onOpen={setOpen} />}
      <WriteSection />
      <Lightbox item={open} onClose={() => setOpen(null)} />
    </div>
  );
}
